import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../utils/cn";
import { currencySymbol } from "../../utils/currency";
import { useScrollPast } from "../../hooks/useScrollPast";
import { LocaleFlag } from "./LocaleFlag";
import {
	useLocaleViewModel,
	type LocaleCatalogEntry,
	type SwitcherStyle,
} from "../../viewmodels/useLocaleViewModel";

/**
 * Shared chooser used by BOTH presentations (issue #140):
 *   - desktop: rendered inside an absolute popover under the header pill
 *   - mobile:  rendered inline as the last row of the nav drawer
 *
 * WAI-ARIA menu-button pattern: `role="menu"` + `role="menuitemradio"`
 * (the locales are a single-select set, so the current one is aria-checked).
 * Roving tabindex + Arrow/Home/End navigation; focus enters the menu on open
 * and Escape/Tab returns it to the trigger via `onClose`.
 */
export interface LocaleOptionsMenuProps {
	options: LocaleCatalogEntry[];
	currentLocale: string;
	menuId: string;
	labelledBy: string;
	onSelect: (locale: string) => void;
	onClose: () => void;
	className?: string;
}

export const LocaleOptionsMenu: React.FC<LocaleOptionsMenuProps> = ({
	options,
	currentLocale,
	menuId,
	labelledBy,
	onSelect,
	onClose,
	className,
}) => {
	const { t } = useTranslation();
	const currentIndex = Math.max(
		0,
		options.findIndex((o) => o.locale === currentLocale),
	);
	const [focusedIndex, setFocusedIndex] = useState(currentIndex);
	const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

	// Move DOM focus to the active item whenever it changes (incl. on open).
	useEffect(() => {
		itemRefs.current[focusedIndex]?.focus();
	}, [focusedIndex]);

	const onKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
		switch (event.key) {
			case "ArrowDown":
				event.preventDefault();
				setFocusedIndex((i) => (i + 1) % options.length);
				break;
			case "ArrowUp":
				event.preventDefault();
				setFocusedIndex((i) => (i - 1 + options.length) % options.length);
				break;
			case "Home":
				event.preventDefault();
				setFocusedIndex(0);
				break;
			case "End":
				event.preventDefault();
				setFocusedIndex(options.length - 1);
				break;
			case "Escape":
			case "Tab":
				onClose();
				break;
			default:
				break;
		}
	};

	return (
		<ul
			id={menuId}
			role="menu"
			aria-labelledby={labelledBy}
			aria-orientation="vertical"
			data-testid="locale-switcher-popover"
			onKeyDown={onKeyDown}
			className={cn("list-none m-0 p-1", className)}
		>
			{options.map((option, index) => {
				const isCurrent = option.locale === currentLocale;
				const optionLabel = t(`locale.switcher.option.${option.locale}`, option.locale);
				return (
					<li key={option.locale} role="none">
						<button
							type="button"
							role="menuitemradio"
							aria-checked={isCurrent}
							tabIndex={index === focusedIndex ? 0 : -1}
							ref={(el) => {
								itemRefs.current[index] = el;
							}}
							data-testid={`locale-switcher-option-${option.locale}`}
							onClick={() => onSelect(option.locale)}
							className={cn(
								"w-full text-left px-3 py-2.5 rounded-sm bg-transparent border-none min-h-[44px]",
								"flex items-center gap-2 text-text-primary cursor-pointer hover:bg-text-secondary/10",
								"focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary",
								isCurrent && "font-semibold",
							)}
						>
							<LocaleFlag locale={option.locale} className="w-5 h-auto rounded-[1px] shrink-0" />
							<span data-i18n-key={`locale.switcher.option.${option.locale}`}>
								{optionLabel}
							</span>
							<span className="text-text-secondary">
								{currencySymbol(option.currency, option.locale)}
							</span>
							{isCurrent && (
								<svg
									className="ml-auto text-primary"
									width="16"
									height="16"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2.5"
									aria-hidden="true"
									focusable="false"
								>
									<path d="M20 6 9 17l-5-5" />
								</svg>
							)}
						</button>
					</li>
				);
			})}
		</ul>
	);
};

interface PillTriggerProps {
	style: SwitcherStyle;
	pillText: string;
	currencySym: string;
	ariaLabel: string;
	scrolled: boolean;
	expanded: boolean;
	menuId: string;
	onClick: () => void;
	onOpenKey: () => void;
	currentLocale: string;
	buttonId: string;
}

/**
 * Desktop pill trigger. Variant driven by runtime-config `locales.switcherStyle`:
 * pill = flag + code/currency, minimal = text-only, iconOnly = flag-only.
 */
const PillTrigger: React.FC<PillTriggerProps> = ({
	style,
	pillText,
	currencySym,
	ariaLabel,
	scrolled,
	expanded,
	menuId,
	onClick,
	onOpenKey,
	currentLocale,
	buttonId,
}) => {
	const shrinkToFlagOnly = scrolled || style === "iconOnly";
	const showFlag = style === "pill" || style === "iconOnly";
	const showText = !shrinkToFlagOnly && (style === "pill" || style === "minimal");
	return (
		<button
			id={buttonId}
			type="button"
			data-testid="locale-switcher-pill"
			aria-label={ariaLabel}
			aria-haspopup="menu"
			aria-expanded={expanded}
			aria-controls={menuId}
			onClick={onClick}
			onKeyDown={(e) => {
				if (!expanded && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
					e.preventDefault();
					onOpenKey();
				}
			}}
			className={cn(
				"inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm min-h-[44px]",
				"border border-text-secondary/60 hover:border-text-primary",
				"text-text-primary bg-transparent cursor-pointer transition-colors",
				"focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
				shrinkToFlagOnly && "px-2",
				style === "minimal" && "border-none px-2",
			)}
		>
			{showFlag && <LocaleFlag locale={currentLocale} className="w-5 h-auto rounded-[1px]" />}
			{showText && (
				<span>
					<span data-i18n-key="locale.switcher.pill">{pillText}</span>
					{" · "}
					<span>{currencySym}</span>
				</span>
			)}
		</button>
	);
};

export interface LocaleSwitcherProps {
	/** "popover" (desktop header pill) or "inline" (mobile drawer row). */
	presentation?: "popover" | "inline";
}

/**
 * LocaleSwitcher — a language + currency chooser. Both presentations share
 * `LocaleOptionsMenu` (same keyboard a11y + selection behaviour), so desktop
 * and mobile use one interaction model: open a chooser, pick a locale.
 */
export const LocaleSwitcher: React.FC<LocaleSwitcherProps> = ({ presentation = "popover" }) => {
	const buttonId = useId();
	const menuId = useId();
	const { t } = useTranslation();
	const viewModel = useLocaleViewModel();
	const scrolled = useScrollPast(40);
	const [open, setOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	const close = useCallback((returnFocus = true) => {
		setOpen(false);
		if (returnFocus) document.getElementById(buttonId)?.focus();
	}, [buttonId]);

	// Close on outside pointerdown / Escape. pointerdown (not mousedown) so a
	// click on a sibling header control (e.g. the cart button) isn't swallowed
	// by an outside-close that unmounts the menu mid-interaction.
	useEffect(() => {
		if (!open) return undefined;
		const handlePointer = (event: PointerEvent) => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setOpen(false);
			}
		};
		const handleKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") close();
		};
		document.addEventListener("pointerdown", handlePointer);
		document.addEventListener("keydown", handleKey);
		return () => {
			document.removeEventListener("pointerdown", handlePointer);
			document.removeEventListener("keydown", handleKey);
		};
	}, [open, close]);

	const onSelect = useCallback(
		(locale: string) => {
			viewModel.setLocale(locale);
			close();
		},
		[viewModel, close],
	);

	if (!viewModel.isReady || viewModel.availableLocales.length === 0) {
		return null;
	}

	const pillText = t("locale.switcher.pill", "ENG");
	const ariaLabel = t("locale.switcher.ariaLabel", "Open language and currency switcher");
	// Currency is data-driven from the region catalog (Medusa region currency),
	// rendered as its narrow symbol via Intl — not hardcoded in translations.
	const currentCurrency =
		viewModel.availableLocales.find((o) => o.locale === viewModel.currentLocale)?.currency ?? "";
	const currentCurrencySym = currencySymbol(currentCurrency, viewModel.currentLocale);

	const menu = open ? (
		<LocaleOptionsMenu
			options={viewModel.availableLocales}
			currentLocale={viewModel.currentLocale}
			menuId={menuId}
			labelledBy={buttonId}
			onSelect={onSelect}
			onClose={() => close()}
			className={
				presentation === "popover"
					? "absolute right-0 mt-2 min-w-[14rem] rounded-md border border-text-secondary/40 bg-background-primary shadow-lg z-50"
					: "mt-1"
			}
		/>
	) : null;

	if (presentation === "inline") {
		// Mobile drawer row: full-width trigger + inline-expanding chooser.
		return (
			<div className="mt-2 border-t border-border pt-2" ref={containerRef}>
				<button
					id={buttonId}
					type="button"
					data-testid="mobile-nav-locale-row"
					aria-label={ariaLabel}
					aria-haspopup="menu"
					aria-expanded={open}
					aria-controls={menuId}
					onClick={() => setOpen((p) => !p)}
					className={cn(
						"w-full text-left px-2 py-2.5 min-h-[44px] text-base text-text-primary",
						"bg-transparent border-none cursor-pointer hover:text-primary transition-colors",
						"flex items-center justify-between",
						"focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary",
					)}
				>
					<span className="flex items-center gap-2">
						<LocaleFlag locale={viewModel.currentLocale} className="w-5 h-auto rounded-[1px]" />
						<span>
							<span data-i18n-key="locale.switcher.pill">{pillText}</span>
							{" · "}
							<span>{currentCurrencySym}</span>
						</span>
					</span>
					<svg
						className={cn("transition-transform", open && "rotate-90")}
						width="18"
						height="18"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						aria-hidden="true"
						focusable="false"
					>
						<path d="m9 18 6-6-6-6" />
					</svg>
				</button>
				<span className="sr-only" data-i18n-key="locale.switcher.label" data-testid="locale-switcher-label">
					{t("locale.switcher.label", "Language")}
				</span>
				{menu}
			</div>
		);
	}

	// Desktop header pill + popover chooser.
	return (
		<div className="relative" ref={containerRef}>
			<PillTrigger
				style={viewModel.switcherStyle}
				pillText={pillText}
				currencySym={currentCurrencySym}
				ariaLabel={ariaLabel}
				scrolled={scrolled}
				expanded={open}
				menuId={menuId}
				onClick={() => setOpen((p) => !p)}
				onOpenKey={() => setOpen(true)}
				currentLocale={viewModel.currentLocale}
				buttonId={buttonId}
			/>
			<span className="sr-only" data-i18n-key="locale.switcher.label" data-testid="locale-switcher-label">
				{t("locale.switcher.label", "Language")}
			</span>
			{menu}
		</div>
	);
};

export default LocaleSwitcher;
